/**
 * Per-pre-order AI in-flight store.
 *
 * `useAiPreOrderActions(preOrderId)` returns a React-local state for
 * `isApproving` / `isDeclining`. That state is scoped to whichever
 * component instance calls the hook — so a sibling component that ALSO
 * calls `useAiPreOrderActions(preOrderId)` gets its own independent
 * state. That's a problem when:
 *
 *   1. `ModifyControls` calls the hook to fire approve/decline and
 *      shows its own spinner from the hook's local state.
 *   2. `PreOrderSection` calls the hook to feed `usePreOrderLock` so
 *      the section disables every editable surface while AI is in
 *      flight (extends the existing `tableDisabled` + the header's
 *      per-input `disabled` / `readOnly` props).
 *
 * Both instances are wired to the same `preOrderId` but their state
 * doesn't sync — clicking Accept in (1) flips its local `isApproving`
 * to true, while (2)'s `isApproving` stays false. Result: the lock
 * never fires and the user can keep editing through the round-trip
 * + SSE settle window (cubic PR #1467 review).
 *
 * Fix: a module-level singleton that mirrors the local React state.
 * `useAiPreOrderActions` writes to it; `usePreOrderLock` reads from
 * it. Per-PO subscription keeps re-renders surgical — editing flow X
 * doesn't re-render flow Y's section. Module-level (not provider-
 * scoped) because AI in-flight is intrinsically global state — there
 * is only ever one server-side `pg_advisory_xact_lock` per PO, and
 * the FE should reflect that single source of truth too.
 *
 * The recap action bar's AI transitions (`actions/transitions.ts`,
 * which resolve locally instead of going through the hook) also write
 * here so per-PO sections lock while a decision is in flight.
 */

import { useSyncExternalStore } from "react";

export interface AiInFlightSlice {
  isApprovingAi: boolean;
  isDecliningAi: boolean;
}

const EMPTY_SLICE: AiInFlightSlice = Object.freeze({
  isApprovingAi: false,
  isDecliningAi: false,
});

type Listener = () => void;

class AiInFlightStore {
  private slices = new Map<string, AiInFlightSlice>();
  private listeners = new Set<Listener>();

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  getSlice = (preOrderId: string): AiInFlightSlice => {
    return this.slices.get(preOrderId) ?? EMPTY_SLICE;
  };

  setApprovingAi(preOrderId: string, value: boolean): void {
    this.update(preOrderId, (s) => ({ ...s, isApprovingAi: value }));
  }

  setDecliningAi(preOrderId: string, value: boolean): void {
    this.update(preOrderId, (s) => ({ ...s, isDecliningAi: value }));
  }

  private update(
    preOrderId: string,
    fn: (s: AiInFlightSlice) => AiInFlightSlice,
  ): void {
    const current = this.slices.get(preOrderId) ?? EMPTY_SLICE;
    const next = fn(current);
    if (
      next.isApprovingAi === current.isApprovingAi &&
      next.isDecliningAi === current.isDecliningAi
    ) {
      return;
    }
    // Drop fully-idle slices so the map doesn't accumulate one entry per
    // PO ever touched in the session.
    if (!next.isApprovingAi && !next.isDecliningAi) {
      this.slices.delete(preOrderId);
    } else {
      this.slices.set(preOrderId, next);
    }
    for (const l of this.listeners) l();
  }
}

const aiInFlightStore = new AiInFlightStore();

/** Imperative API for non-React callers (the hook itself + action transitions). */
export const aiInFlight = {
  setApprovingAi: (preOrderId: string, value: boolean): void => {
    aiInFlightStore.setApprovingAi(preOrderId, value);
  },
  setDecliningAi: (preOrderId: string, value: boolean): void => {
    aiInFlightStore.setDecliningAi(preOrderId, value);
  },
  getSlice: (preOrderId: string): AiInFlightSlice => {
    return aiInFlightStore.getSlice(preOrderId);
  },
};

/**
 * Subscribe to the AI in-flight slice for a single pre-order. Re-renders
 * only when *this* PO's slice changes — siblings stay stable.
 */
export function useAiInFlightSlice(preOrderId: string): AiInFlightSlice {
  return useSyncExternalStore(aiInFlightStore.subscribe, () =>
    aiInFlightStore.getSlice(preOrderId),
  );
}
